import { Controller } from "@hotwired/stimulus";

/**
 * Quantity controller that keeps a numeric input in sync while emitting
 * `quantity:change` events whenever the displayed value changes.
 *
 * NOTE: The `input` target is required for this controller to function.
 * Make sure the markup includes `data-quantity-target="input"`.
 */
export default class extends Controller {
  static targets = ["input"];
  static values = {
    min: Number,
    max: Number,
    step: Number,
  };

  /**
   * Cache a quick reference to the input element to avoid repetition.
   */
  get input() {
    return this.inputTarget;
  }

  /**
   * Stimulus lifecycle hook.
   * Normalizes the initial value without emitting events so the view starts clean.
   */
  connect() {
    // Track the last committed value so manual typing can emit changes reliably.
    this._lastValue = undefined;
    this._syncFromInput({ emit: false });
  }

  /**
   * Increase the quantity by `step` when the "+" control is clicked.
   */
  increment(event) {
    event.preventDefault();
    this._adjustBy(this._step());
  }

  /**
   * Decrease the quantity by `step` when the "-" control is clicked.
   */
  decrement(event) {
    event.preventDefault();
    this._adjustBy(-this._step());
  }

  /**
   * React to raw input events (typing, paste, etc.) and normalize the value.
   */
  onInput() {
    this._syncFromInput({ emit: true });
  }

  /**
   * React to change events triggered by mobile keyboards or blur.
   */
  onChange() {
    this._syncFromInput({ emit: true });
  }

  /**
   * Apply a positive or negative delta to the current value and broadcast the result.
   */
  _adjustBy(delta) {
    const next = this._clamp(this._currentValue() + delta);
    this._commit(next, { emit: true });
  }

  /**
   * Normalize direct user input (typing/pasting) to a valid number.
   * Optionally emits a `quantity:change` event when the value actually changes.
   */
  _syncFromInput({ emit }) {
    const coerced = this._clamp(this._coerceNumber(this.input.value, this._min()));
    this._commit(coerced, { emit });
  }

  /**
   * Returns the current numeric value shown in the field.
   */
  _currentValue() {
    return this._coerceNumber(this.input.value, this._min());
  }

  /**
   * Write the sanitized number back to the input and emit once per actual change.
   */
  _commit(value, { emit }) {
    const normalized = Number.isFinite(value) ? value : this._min();
    const changed = normalized !== this._lastValue;
    this.input.value = String(normalized);
    if (emit && changed) {
      this._dispatch(normalized);
    }
    this._lastValue = normalized;
  }

  /**
   * Dispatch a bubbling event so other controllers (cart, etc.) can react.
   */
  _dispatch(value) {
    this.input.dispatchEvent(
      new CustomEvent("quantity:change", {
        detail: { value, input: this.input },
        bubbles: true,
      })
    );
  }

  /**
   * Clamp the provided number inside the configured min/max bounds.
   */
  _clamp(value) {
    const min = this._min();
    const max = this._max();
    if (Number.isFinite(min) && value < min) return min;
    if (Number.isFinite(max) && value > max) return max;
    return value;
  }

  /**
   * Resolve the minimum value from controller values or input attributes.
   */
  _min() {
    if (this.hasMinValue) return Number(this.minValue);
    const attr = Number(this.input.getAttribute("min"));
    if (Number.isFinite(attr)) return attr;
    // Default minimum of 1 keeps empty inputs or invalid data from dropping to zero.
    return 1;
  }

  /**
   * Resolve the maximum value from controller values or input attributes.
   */
  _max() {
    if (this.hasMaxValue) return Number(this.maxValue);
    const attr = Number(this.input.getAttribute("max"));
    if (Number.isFinite(attr)) return attr;
    return Number.POSITIVE_INFINITY;
  }

  /**
   * Determine the step used when incrementing or decrementing the quantity.
   */
  _step() {
    if (this.hasStepValue) {
      const fromValue = Number(this.stepValue);
      if (Number.isFinite(fromValue) && fromValue > 0) return fromValue;
    }
    const attr = Number(this.input.getAttribute("step"));
    if (Number.isFinite(attr) && attr > 0) return attr;
    // Fall back to a step of 1 so you always move the value forward.
    return 1;
  }

  /**
   * Convert arbitrary input to a finite number or fall back to a default.
   */
  _coerceNumber(value, fallback) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  }
}
